import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Loader, Expand } from 'lucide-react';
import Select from '@cloudscape-design/components/select';
import PerformanceChart from './PerformanceChart';
import pako from 'pako';

const truncateText = (text, maxLength = 20) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

const getFilteredData = (timeRange) => {
  let days = 1;
  if (timeRange === "1h") days = 0.05;
  else if (timeRange === "3h") days = 0.125;
  else if (timeRange === "12h") days = 0.5;
  else if (timeRange === "1d") days = 1;
  else if (timeRange === "3d") days = 3;
  else if (timeRange === "1w") days = 7;
  else if (timeRange.startsWith("custom:")) {
    const [, startIso, endIso] = timeRange.split(":");
    const start = new Date(startIso);
    const end = new Date(endIso);
    days = (end - start) / (1000 * 60 * 60 * 24);
  }
  return days.toString();
};

const metricsQuery = `
  query MyQuery($accountNo: String, $duration: String, $metricName: String, $region: String, $resourceNames: [String], $serviceName: String, $stat: String, $startDate:String,$endDate:String) {
    getMetricsData(
      accountNo: $accountNo
      duration: $duration
      startDate:$startDate
      endDate:$endDate
      metricName: $metricName
      region: $region
      resourceNames: $resourceNames
      serviceName: $serviceName
      stat: $stat
    )
    {
    compressionType
    data
    encoding
    error
    isCompressed
  }
  }
`;

const MetricCard = ({ 
  namespace, 
  metricName, 
  resourceNames, 
  api, 
  selectedTime, 
  dashboardData, 
  onExpand, 
  isExpanded = false,
  height,
  initialData = null,
  timeFilter = null
}) => {
  const [metricData, setMetricData] = useState(initialData || []);
  const [isLoading, setIsLoading] = useState(initialData ? false : true);
  const [error, setError] = useState(null);
  const [selectedStat, setSelectedStat] = useState(null);
  const [displayStat, setDisplayStat] = useState(initialData ? (initialData[0]?.stat || "Fetching...") : "Fetching...");
  const [lastFetchedTime, setLastFetchedTime] = useState(selectedTime);
  const [shouldFetch, setShouldFetch] = useState(initialData ? false : true);
  const abortControllerRef = useRef(null);

  const chartHeight = height || (isExpanded ? 500 : 200);

  const decompressData = (compressedResponse) => {
    try {
      if (!compressedResponse || !compressedResponse.isCompressed) {
        console.warn('Response is not compressed');
        return compressedResponse?.data || compressedResponse;
      }
      if (compressedResponse.compressionType === 'gzip' && compressedResponse.encoding === 'base64') {
        const binaryString = atob(compressedResponse.data);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const decompressedData = pako.inflate(bytes, { to: 'string' });
        const parsedData = JSON.parse(decompressedData);
        return parsedData;
      } else {
        throw new Error(`Unsupported compression: ${compressedResponse.compressionType}/${compressedResponse.encoding}`);
      }
    } catch (error) {
      console.error('Error decompressing data:', error);
      throw new Error(`Failed to decompress response: ${error.message}`);
    }
  };

  useEffect(() => {
    if (selectedTime !== lastFetchedTime || selectedStat !== null) {
      setShouldFetch(true);
      setError(null); // Clear error when a new fetch is triggered
    }
  }, [selectedTime, selectedStat, lastFetchedTime]);

  const fetchMetricData = useCallback(async () => {
    if (!shouldFetch) {
      return;
    }
    setIsLoading(true);
    setError(null); // Clear error before starting fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      let duration = getFilteredData(selectedTime);
      let startDate = null;
      let endDate = null;

      if (selectedTime && selectedTime.startsWith("custom:")) {
        duration = null;
        const [, startIso, endIso] = selectedTime.split(":");
        startDate = startIso;
        endDate = endIso;
      }

      const variables = {
        accountNo: dashboardData.accountNo,
        duration,
        startDate,
        endDate,
        metricName,
        region: dashboardData.region,
        resourceNames,
        serviceName: namespace,
        stat: selectedStat 
      };

      const response = await api.post(
        '', 
        { query: metricsQuery, variables },
        { signal: abortController.signal }
      );
      if (response.data?.data?.getMetricsData) {
        const decompressedData = decompressData(response.data.data.getMetricsData);
        setMetricData(decompressedData);
        setDisplayStat(decompressedData[0]?.stat);
        setLastFetchedTime(selectedTime);
        // setShouldFetch(false);
      } else if (response.data?.errors) {
        throw new Error(response.data.errors[0]?.message || "Unknown API error");
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      console.error(`Error fetching metrics data for ${metricName}:`, error);
      setError(`Failed to load data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [api, dashboardData, metricName, resourceNames, selectedStat, selectedTime, namespace, shouldFetch]);

  useEffect(() => {
    fetchMetricData();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchMetricData]);

  const handleStatChange = useCallback(({ detail }) => {
    if (detail.selectedOption && detail.selectedOption.value) {
      setSelectedStat(detail.selectedOption.value);
    }
  }, []);

  const uniqueResourceNames = useMemo(() => 
    [...new Set(metricData.map(item => item.resourceName))],
    [metricData]
  );

  const statOptions = [
    { value: "avg", label: "avg" },
    { value: "sum", label: "sum" },
    { value: "min", label: "min" },
    { value: "max", label: "max" },
    { value: "count", label: "count" }
  ];

  const chartComponent = useMemo(() => {
    if (isLoading) {
      return (
        <div 
          className="flex items-center justify-center" 
          style={{ height: `${chartHeight+105}px` }}
        >
          <Loader className="h-8 w-8 text-blue-500 animate-spin" />
        </div>
      );
    } else if (error) {
      return (
        <div 
          className="flex items-center justify-center text-red-500" 
          style={{ height: `${chartHeight}px` }}
        >
          {error}
        </div>
      );
    } else {
      return (
        <PerformanceChart
          title={metricName}
          data={metricData}
          height={chartHeight}
          legend={uniqueResourceNames}
          timeRange={selectedTime}
          isExpanded={isExpanded}
        />
      );
    }
  }, [error, chartHeight, isExpanded, isLoading, metricData, metricName, selectedTime, uniqueResourceNames]);
  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <h3 
          className="text-base font-medium text-gray-700 truncate max-w-[150px]"
          title={metricName}
        >
          {isExpanded ? "" : truncateText(metricName, 18)}
        </h3>
        <div className="flex items-center gap-2">
          {timeFilter && <div className="mr-2">{timeFilter}</div>}
          
          <Select
            selectedOption={{value:displayStat}}
            onChange={handleStatChange}
            options={statOptions}
            ariaLabel="Select statistic"
          />
          
          {!isExpanded && (
            <button 
              onClick={() => onExpand(metricData)}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              aria-label="Expand graph"
              title="Expand graph"
            >
              <Expand className="h-4 w-4 text-gray-600" />
            </button>
          )}
        </div>
      </div>
      
      <div className="chart-container">
        {chartComponent}
      </div>
    </>
  );
};

export default React.memo(MetricCard, (prevProps, nextProps) => {
  const timeFilterEqual = 
    (!prevProps.timeFilter && !nextProps.timeFilter) || 
    (prevProps.timeFilter === nextProps.timeFilter);
    
  return (
    prevProps.selectedTime === nextProps.selectedTime &&
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.height === nextProps.height &&
    prevProps.namespace === nextProps.namespace &&
    prevProps.metricName === nextProps.metricName &&
    JSON.stringify(prevProps.resourceNames) === JSON.stringify(nextProps.resourceNames) &&
    timeFilterEqual
  );
});